import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#1a3c2a] py-12 text-center text-[#faf8f0]/80" data-id="element-23">
        <div className="max-w-4xl mx-auto px-4" data-id="element-24">
          <p className="mb-4 font-medium text-white" data-id="element-25">
            Download instantly. Recipes work when you need them.
          </p>
          <p className="text-sm opacity-60 mb-6" data-id="element-26">
            This product is for educational purposes. Not medical advice.
            Consult your healthcare provider.
          </p>
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-27">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-[#c9a84c] underline underline-offset-2 hover:text-[#d4af37] transition-colors" data-id="element-28">
              Privacy Policy
            </button>
            <span className="text-[#faf8f0]/30" data-id="element-29">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-[#c9a84c] underline underline-offset-2 hover:text-[#d4af37] transition-colors" data-id="element-30">
              Terms of Service
            </button>
          </div>
          <p className="text-sm opacity-40" data-id="element-31">
            © 2024 Natural Remedies Encyclopedia. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-32">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-33">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-[#faf8f0] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-34">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a3c2a]/10 bg-[#1a3c2a]" data-id="element-35">
                <h3 className="text-xl font-bold text-[#c9a84c]" data-id="element-36">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-[#faf8f0]/70 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10" data-id="element-37">
                  <X className="w-5 h-5" data-id="element-38" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-[#5c4a32] text-sm leading-relaxed space-y-4" data-id="element-39">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-40" /> : <TermsContent data-id="element-41" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-[#5c4a32]/60" data-id="element-42">Last updated: January 2024</p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-43">
        1. Information We Collect
      </h4>
      <p data-id="element-44">
        When you purchase the Natural Remedies Encyclopedia, we collect only the
        information necessary to process your order, including your name, email
        address, and payment details. Payment processing is handled by secure
        third-party providers — we do not store your credit card information.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-45">
        2. How We Use Your Information
      </h4>
      <p data-id="element-46">We use your information to:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-47">
        <li data-id="element-48">Deliver your digital product and download link</li>
        <li data-id="element-49">Send purchase confirmation and receipt</li>
        <li data-id="element-50">Provide customer support if you reach out</li>
        <li data-id="element-51">Send product updates (only if you opt in)</li>
      </ul>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-52">
        3. Information Sharing
      </h4>
      <p data-id="element-53">
        We do not sell, trade, or rent your personal information to third
        parties. We may share information only with trusted service providers
        who assist in operating our business (e.g., payment processors, email
        delivery), and only to the extent necessary.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-54">4. Cookies</h4>
      <p data-id="element-55">
        Our website may use cookies to improve your browsing experience and
        analyze site traffic. You can disable cookies in your browser settings
        at any time.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-56">
        5. Data Security
      </h4>
      <p data-id="element-57">
        We implement reasonable security measures to protect your personal
        information. However, no method of transmission over the internet is
        100% secure, and we cannot guarantee absolute security.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-58">
        6. Your Rights
      </h4>
      <p data-id="element-59">
        You may request access to, correction of, or deletion of your personal
        data at any time. To exercise these rights, please contact us through
        our support channels.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-60">
        7. Changes to This Policy
      </h4>
      <p data-id="element-61">
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page with an updated revision date.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-[#5c4a32]/60" data-id="element-62">Last updated: January 2024</p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-63">
        1. Product Description
      </h4>
      <p data-id="element-64">
        The Natural Remedies Encyclopedia is a digital educational product (PDF
        format) containing natural remedy recipes, preparation methods, and
        wellness information. It is intended for informational and educational
        purposes only.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-65">
        2. Medical Disclaimer
      </h4>
      <p data-id="element-66">
        This product is NOT a substitute for professional medical advice,
        diagnosis, or treatment. Always seek the advice of your physician or
        other qualified health provider with any questions you may have
        regarding a medical condition. Never disregard professional medical
        advice or delay seeking it because of information contained in this
        product.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-67">
        3. Digital Delivery
      </h4>
      <p data-id="element-68">
        Upon purchase, you will receive instant access to download the digital
        product. Due to the digital nature of this product, delivery is
        immediate and automated.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-69">
        4. Refund Policy
      </h4>
      <p data-id="element-70">
        We offer a 30-day money-back guarantee. If you are not satisfied with
        the product for any reason, you may request a full refund within 30 days
        of purchase. Refund requests can be submitted through our support
        channels.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-71">
        5. Intellectual Property
      </h4>
      <p data-id="element-72">
        All content within the Natural Remedies Encyclopedia is protected by
        copyright. You may not reproduce, distribute, modify, or create
        derivative works from this product without prior written consent. The
        product is licensed for personal, non-commercial use only.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-73">
        6. Limitation of Liability
      </h4>
      <p data-id="element-74">
        To the fullest extent permitted by law, we shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages
        arising from your use of this product. Your use of any information or
        remedies from this product is at your own risk.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-75">
        7. Governing Law
      </h4>
      <p data-id="element-76">
        These terms shall be governed by and construed in accordance with the
        laws of the United States. Any disputes arising from these terms will be
        resolved in the appropriate courts.
      </p>

      <h4 className="font-bold text-[#1a3c2a] text-base mt-4" data-id="element-77">
        8. Changes to Terms
      </h4>
      <p data-id="element-78">
        We reserve the right to modify these terms at any time. Continued use of
        the product after changes constitutes acceptance of the updated terms.
      </p>
    </>;
}